/**
 * 用户组 presence：onlineCount = members Hash 中 presence=online（与 currentGroup 无关）。
 * 进入组视图：current/select（音频组态）+ 订阅 presence MQTT + GET /presence。
 * 离开组视图：current/clear + 取消订阅；onlineCount 不因 clear 变化。
 */

import { ApiClient } from '../utils/apiClient';
import { mqttService } from './mqttService';
import { mqttSessionPresenceService } from './mqttSessionPresenceService';
import { logger } from '../utils/logger';

export type GroupPresenceState = {
  memberCount: number;
  onlineCount: number;
  updatedAt: number | null;
  loading: boolean;
  error: string | null;
};

export type GroupPresenceDownstream = {
  groupNumber: string;
  memberCount: number;
  onlineCount: number;
  occurredAt: number;
};

type PresenceListener = (groupNumber: string, state: GroupPresenceState) => void;

const EMPTY_STATE: GroupPresenceState = {
  memberCount: 0,
  onlineCount: 0,
  updatedAt: null,
  loading: false,
  error: null,
};

class VoiceGroupPresenceService {
  private activeGroupNumber: string | null = null;
  private activeUserId: string | null = null;
  private presenceTopic: string | null = null;
  private messageUnsub: (() => void) | null = null;
  private stateByGroup = new Map<string, GroupPresenceState>();
  private listeners = new Set<PresenceListener>();
  private enterPromise: Promise<void> | null = null;
  private leavePromise: Promise<void> | null = null;

  subscribe(listener: PresenceListener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  getState(groupNumber: string): GroupPresenceState {
    return this.stateByGroup.get(groupNumber) ?? { ...EMPTY_STATE };
  }

  getActiveGroupNumber(): string | null {
    return this.activeGroupNumber;
  }

  async enterGroupView(groupNumber: string, userId: string | number): Promise<void> {
    const normalizedGroup = String(groupNumber).trim();
    const normalizedUserId = String(userId).trim();
    if (!normalizedGroup || !normalizedUserId) return;

    if (this.leavePromise) {
      await this.leavePromise;
    }

    if (
      this.activeGroupNumber === normalizedGroup &&
      this.activeUserId === normalizedUserId &&
      this.presenceTopic
    ) {
      await this.fetchPresenceSnapshot(normalizedGroup);
      return;
    }

    if (this.enterPromise) {
      await this.enterPromise;
      if (
        this.activeGroupNumber === normalizedGroup &&
        this.activeUserId === normalizedUserId
      ) {
        return;
      }
    }

    this.enterPromise = this.performEnter(normalizedGroup, normalizedUserId);
    try {
      await this.enterPromise;
    } finally {
      this.enterPromise = null;
    }
  }

  async leaveGroupView(): Promise<void> {
    if (this.leavePromise) {
      await this.leavePromise;
      return;
    }
    if (!this.activeGroupNumber) return;

    this.leavePromise = this.performLeave();
    try {
      await this.leavePromise;
    } finally {
      this.leavePromise = null;
    }
  }

  /** 仅当当前活跃组与参数一致时才 clear，避免 effect 重跑误清新会话 */
  async leaveGroupViewIfActive(groupNumber: string): Promise<void> {
    if (!this.activeGroupNumber) return;
    if (this.activeGroupNumber !== String(groupNumber).trim()) return;
    await this.leaveGroupView();
  }

  /** 前台恢复时校准当前组 presence（§3.3 可选） */
  async refreshPresenceSnapshot(groupNumber: string): Promise<void> {
    const normalizedGroup = String(groupNumber).trim();
    if (!normalizedGroup) return;
    if (this.activeGroupNumber !== normalizedGroup) return;
    await this.fetchPresenceSnapshot(normalizedGroup);
  }

  /** MQTT 重连后恢复组 presence 订阅并校准首屏 */
  onMqttConnected(): void {
    if (!this.activeGroupNumber || !this.activeUserId || !this.presenceTopic) return;

    void (async () => {
      try {
        await mqttSessionPresenceService.ensureReady();
        const mgr = mqttService.getMqttManager();
        await mgr.subscribe(this.presenceTopic!, 1);
        await this.fetchPresenceSnapshot(this.activeGroupNumber!);
      } catch (error) {
        logger.warn('MQTT 重连后恢复组 presence 失败:', error);
      }
    })();
  }

  deactivate(): void {
    void this.performLeave().catch(() => undefined);
    this.stateByGroup.clear();
    this.listeners.clear();
  }

  private async performEnter(groupNumber: string, userId: string): Promise<void> {
    const switchingGroup = Boolean(
      this.activeGroupNumber &&
      (this.activeGroupNumber !== groupNumber || this.activeUserId !== userId),
    );
    if (switchingGroup) {
      await this.performLeave();
    }

    this.activeGroupNumber = groupNumber;
    this.activeUserId = userId;
    this.presenceTopic = `voice-group/${groupNumber}/${userId}/presence/downstream`;

    this.patchState(groupNumber, { loading: true, error: null });

    try {
      await mqttSessionPresenceService.ensureReady();

      await ApiClient.post('/userGroup/current/select', { groupNumber });

      this.ensureMessageHandler();

      const mgr = mqttService.getMqttManager();
      await mgr.subscribe(this.presenceTopic, 1);

      await this.fetchPresenceSnapshot(groupNumber);
    } catch (error) {
      const message = error instanceof Error ? error.message : '进入组 presence 失败';
      this.patchState(groupNumber, { loading: false, error: message });
      throw error;
    }
  }

  private async performLeave(): Promise<void> {
    const groupNumber = this.activeGroupNumber;
    const topic = this.presenceTopic;

    if (!groupNumber) {
      return;
    }

    this.activeGroupNumber = null;
    this.activeUserId = null;
    this.presenceTopic = null;

    if (this.messageUnsub) {
      this.messageUnsub();
      this.messageUnsub = null;
    }

    if (topic && mqttService.isServiceInitialized() && mqttService.isConnected()) {
      try {
        await mqttService.getMqttManager().unsubscribe(topic);
      } catch {
        // 忽略取消订阅失败
      }
    }

    try {
      await ApiClient.post('/userGroup/current/clear', {});
    } catch (error) {
      logger.warn('清空当前组态失败:', error);
    }

    // 仅清订阅态，不向 UI 推送 0/0（onlineCount 与 currentGroup 无关，列表靠 GET /mine 校准）
    this.stateByGroup.delete(groupNumber);
  }

  private ensureMessageHandler(): void {
    if (this.messageUnsub) return;

    const handler = (topic: string, raw: string | Buffer) => {
      if (!this.presenceTopic || topic !== this.presenceTopic) return;
      if (!this.activeGroupNumber) return;

      try {
        const text = typeof raw === 'string' ? raw : raw.toString();
        const payload = JSON.parse(text) as Partial<GroupPresenceDownstream>;
        this.onPresenceMessage(payload, this.activeGroupNumber);
      } catch (error) {
        logger.warn('解析组 presence 下行消息失败:', error);
      }
    };

    this.messageUnsub = mqttService.getMqttManager().addMessageListener(handler);
  }

  private onPresenceMessage(payload: Partial<GroupPresenceDownstream>, activeGroupNumber: string): void {
    const groupNumber = String(payload.groupNumber ?? '').trim();
    if (groupNumber !== activeGroupNumber) return;

    const memberCount = Number(payload.memberCount);
    const onlineCount = Number(payload.onlineCount);
    const occurredAt = Number(payload.occurredAt);

    if (!Number.isFinite(memberCount) || !Number.isFinite(onlineCount)) {
      logger.warn('[presence] 无效计数 payload', payload);
      return;
    }
    if (onlineCount > memberCount) {
      logger.warn('[presence] onlineCount > memberCount，已丢弃', payload);
      return;
    }

    this.patchState(groupNumber, {
      memberCount,
      onlineCount,
      updatedAt: Number.isFinite(occurredAt) ? occurredAt : Date.now(),
      loading: false,
      error: null,
    });
  }

  private async fetchPresenceSnapshot(groupNumber: string): Promise<void> {
    this.patchState(groupNumber, { loading: true, error: null });

    try {
      const response = await ApiClient.get<
        | { success?: boolean; message?: string; data?: GroupPresenceDownstream; timestamp?: number }
        | GroupPresenceDownstream
      >(`/userGroup/${encodeURIComponent(groupNumber)}/presence`);

      const payload = (() => {
        if (response && typeof response === 'object' && 'success' in response) {
          if (response.success === false) {
            throw Object.assign(new Error(response.message || '查询 presence 失败'), { status: 401 });
          }
          return response.data ?? null;
        }
        if (response && typeof response === 'object' && 'memberCount' in response) {
          return response as GroupPresenceDownstream;
        }
        return null;
      })();

      if (!payload) {
        throw new Error('presence 响应为空');
      }

      const memberCount = Number(payload.memberCount);
      const onlineCount = Number(payload.onlineCount);
      if (!Number.isFinite(memberCount) || !Number.isFinite(onlineCount)) {
        throw new Error('presence 计数无效');
      }
      if (onlineCount > memberCount) {
        logger.warn('[presence] HTTP 快照 onlineCount > memberCount', payload);
        return;
      }

      const ts = (
        response &&
        typeof response === 'object' &&
        'timestamp' in response &&
        typeof response.timestamp === 'number'
      ) ? response.timestamp : Date.now();

      this.patchState(groupNumber, {
        memberCount,
        onlineCount,
        updatedAt: Number.isFinite(Number(payload.occurredAt)) ? Number(payload.occurredAt) : ts,
        loading: false,
        error: null,
      });
    } catch (error) {
      const message = error instanceof Error ? error.message : '查询 presence 失败';
      this.patchState(groupNumber, { loading: false, error: message });
      throw error;
    }
  }

  private patchState(groupNumber: string, patch: Partial<GroupPresenceState>): void {
    const prev = this.stateByGroup.get(groupNumber) ?? { ...EMPTY_STATE };
    const next = { ...prev, ...patch };
    this.stateByGroup.set(groupNumber, next);
    this.emit(groupNumber, next);
  }

  private emit(groupNumber: string, state: GroupPresenceState): void {
    this.listeners.forEach((listener) => {
      try {
        listener(groupNumber, state);
      } catch (error) {
        logger.warn('组 presence 监听回调异常:', error);
      }
    });
  }
}

export const voiceGroupPresenceService = new VoiceGroupPresenceService();
